import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CalendarIcon,
  DocumentDuplicateIcon,
  ArrowUpTrayIcon,
  BookmarkIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  PaperClipIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  DocumentTextIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useTimeEntryStore } from '../store/timeEntryStore';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { useTemplateStore } from '../store/templateStore';
import { useTimezoneStore } from '../store/timezoneStore';
import { COMMON_ZONES, formatStartWithZone, zoneLabel } from '../utils/timezone';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Timer } from '../components/time/Timer';
import { TemplateManager } from '../components/TemplateManager';
import { BulkEntryModal } from '../components/BulkEntryModal';
import { CsvImportModal } from '../components/CsvImportModal';
import { TimeEntry, TimeEntryCategory, Category, ProjectSubproject, Attachment, DailyNote, Link, categoriesAPI, subprojectsAPI, attachmentsAPI, dailyNotesAPI, linksAPI, timeEntriesAPI } from '../services/api';
import { PdfViewerModal } from '../components/PdfViewerModal';
import { WeekView } from '../components/time/WeekView';
import { MonthView } from '../components/time/MonthView';
import {
  startOfWeek,
  endOfWeek,
  startOfMonthGrid,
  endOfMonthGrid,
  addDays,
  addMonths,
  formatWeekRange,
  formatMonthYear,
} from '../utils/dateHelpers';

type ViewMode = 'day' | 'week' | 'month';
const VIEW_MODE_STORAGE_KEY = 'mytime.timeEntry.viewMode';

const loadStoredViewMode = (): ViewMode => {
  try {
    const v = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (v === 'day' || v === 'week' || v === 'month') return v;
  } catch {
    // localStorage unavailable (SSR / private mode) — fall through
  }
  return 'day';
};

// Get today's date in YYYY-MM-DD format (local timezone)
const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const TimeEntryPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    dailySummary,
    selectedDate,
    setSelectedDate,
    fetchDailySummary,
    createEntry,
    updateEntry,
    deleteEntry,
    isLoading,
    error,
    clearError,
  } = useTimeEntryStore();
  const { projects, fetchProjects } = useProjectStore();
  const { clients, fetchClients } = useClientStore();
  const { templates, fetchTemplates, applyTemplate } = useTemplateStore();
  const { currentZone, deviceZone, isAuto, setOverride, resetToDevice } = useTimezoneStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState<TimeEntry | null>(null);
  const [formData, setFormData] = useState({
    project_id: '',
    description: '',
    hours: '',
    category: '' as TimeEntryCategory | '',
    date: selectedDate,
    start_time: '' as string,  // Optional start time in HH:MM format
  });

  // Project-scoped categories and subprojects
  const [projectCategories, setProjectCategories] = useState<Category[]>([]);
  const [subprojects, setSubprojects] = useState<ProjectSubproject[]>([]);
  const [selectedSubprojectId, setSelectedSubprojectId] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  // Feature modals state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [isTemplateManagerOpen, setIsTemplateManagerOpen] = useState(false);
  const [isBulkEntryOpen, setIsBulkEntryOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);

  // Sort state
  type SortField = 'modified' | 'start_time' | 'category';
  const [sortField, setSortField] = useState<SortField>('start_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Category color map for daily entries (keyed by category name)
  const [categoryColorMap, setCategoryColorMap] = useState<Record<string, string>>({});

  // Attachment state
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [entryAttachments, setEntryAttachments] = useState<Record<string, Attachment[]>>({});
  const [modalAttachments, setModalAttachments] = useState<Attachment[]>([]);
  const [isLoadingModalAttachments, setIsLoadingModalAttachments] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  // Link state
  const [entryLinks, setEntryLinks] = useState<Record<string, Link[]>>({});
  const [modalLinks, setModalLinks] = useState<Link[]>([]);
  const [isLoadingModalLinks, setIsLoadingModalLinks] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');

  // Daily "Other Notes" state (day view only)
  const [dailyNotes, setDailyNotes] = useState<DailyNote[]>([]);
  const [isLoadingDailyNotes, setIsLoadingDailyNotes] = useState(false);
  const [isUploadingDailyNote, setIsUploadingDailyNote] = useState(false);
  const [dailyNoteError, setDailyNoteError] = useState<string | null>(null);

  // PDF viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [pdfViewerFilename, setPdfViewerFilename] = useState('');
  const [pdfViewerAttachment, setPdfViewerAttachment] = useState<Attachment | null>(null);
  const [pdfViewerDailyNote, setPdfViewerDailyNote] = useState<DailyNote | null>(null);

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchTemplates();
  }, [fetchProjects, fetchClients, fetchTemplates]);

  useEffect(() => {
    fetchDailySummary(selectedDate);
  }, [selectedDate, fetchDailySummary]);

  // ─── View mode (Day / Week / Month) ───
  const [viewMode, setViewMode] = useState<ViewMode>(loadStoredViewMode);
  const [rangeEntries, setRangeEntries] = useState<TimeEntry[]>([]);
  const [isLoadingRange, setIsLoadingRange] = useState(false);
  const [rangeRefreshTick, setRangeRefreshTick] = useState(0);

  // Persist view mode.
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode); } catch { /* noop */ }
  }, [viewMode]);

  // Load day-scoped "Other Notes" for the selected date (day view only).
  useEffect(() => {
    if (viewMode !== 'day') return;
    let cancelled = false;
    setIsLoadingDailyNotes(true);
    setDailyNoteError(null);
    dailyNotesAPI
      .list(selectedDate)
      .then((notes) => {
        if (!cancelled) setDailyNotes(notes);
      })
      .catch(() => {
        if (!cancelled) setDailyNotes([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDailyNotes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate, viewMode]);

  // Compute visible range for the current view.
  const { rangeStart, rangeEnd, weekStartDate, monthRefDate } = useMemo(() => {
    const ref = new Date(selectedDate + 'T00:00:00');
    if (viewMode === 'week') {
      const s = startOfWeek(ref);
      const e = endOfWeek(ref);
      return {
        rangeStart: getLocalDateString(s),
        rangeEnd: getLocalDateString(e),
        weekStartDate: s,
        monthRefDate: ref,
      };
    }
    if (viewMode === 'month') {
      const s = startOfMonthGrid(ref);
      const e = endOfMonthGrid(ref);
      return {
        rangeStart: getLocalDateString(s),
        rangeEnd: getLocalDateString(e),
        weekStartDate: startOfWeek(ref),
        monthRefDate: ref,
      };
    }
    return {
      rangeStart: selectedDate,
      rangeEnd: selectedDate,
      weekStartDate: startOfWeek(ref),
      monthRefDate: ref,
    };
  }, [viewMode, selectedDate]);

  // Fetch entries for the visible range when in Week/Month mode.
  useEffect(() => {
    if (viewMode === 'day') return;
    let cancelled = false;
    setIsLoadingRange(true);
    timeEntriesAPI
      .list({ start_date: rangeStart, end_date: rangeEnd })
      .then((entries) => {
        if (!cancelled) setRangeEntries(entries);
      })
      .catch(() => {
        if (!cancelled) setRangeEntries([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRange(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, rangeStart, rangeEnd, rangeRefreshTick]);

  // Group fetched entries by YYYY-MM-DD for Week/Month consumers.
  const entriesByDate = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    rangeEntries.forEach((e) => {
      const k = e.date;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    });
    // Sort within each day (start_time asc if present, else created_at)
    map.forEach((arr) =>
      arr.sort((a, b) => {
        if (a.start_time && b.start_time) return a.start_time.localeCompare(b.start_time);
        if (a.start_time) return -1;
        if (b.start_time) return 1;
        return (a.created_at || '').localeCompare(b.created_at || '');
      })
    );
    return map;
  }, [rangeEntries]);

  const refreshRange = () => setRangeRefreshTick((n) => n + 1);

  // Build category color map from all projects in daily entries
  useEffect(() => {
    if (!dailySummary?.entries?.length) {
      setCategoryColorMap({});
      return;
    }
    const projectIds = Array.from(new Set(dailySummary.entries.map(e => e.project_id)));
    Promise.all(projectIds.map(pid => categoriesAPI.list(pid).catch(() => [] as Category[])))
      .then(results => {
        const map: Record<string, string> = {};
        results.flat().forEach(cat => {
          if (cat.name && cat.color) map[cat.name] = cat.color;
        });
        setCategoryColorMap(map);
      });
  }, [dailySummary]);

  // Fetch categories and subprojects when project_id changes in form
  useEffect(() => {
    const fetchProjectConfig = async () => {
      if (formData.project_id) {
        try {
          const [cats, subs] = await Promise.all([
            categoriesAPI.list(formData.project_id),
            subprojectsAPI.list(formData.project_id),
          ]);
          setProjectCategories(cats);
          setSubprojects(subs);
        } catch (err) {
          console.error('Failed to fetch project config:', err);
          setProjectCategories([]);
          setSubprojects([]);
        }
      } else {
        setProjectCategories([]);
        setSubprojects([]);
        setSelectedSubprojectId('');
      }
    };
    fetchProjectConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.project_id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const step = direction === 'prev' ? -1 : 1;
    const current = new Date(selectedDate + 'T00:00:00');
    if (viewMode === 'day') {
      current.setDate(current.getDate() + step);
      setSelectedDate(getLocalDateString(current));
    } else if (viewMode === 'week') {
      setSelectedDate(getLocalDateString(addDays(current, step * 7)));
    } else {
      setSelectedDate(getLocalDateString(addMonths(current, step)));
    }
  };

  // Header label adapts to view mode.
  const headerLabel = useMemo(() => {
    if (viewMode === 'day') {
      return formatDate(selectedDate);
    }
    if (viewMode === 'week') {
      return formatWeekRange(weekStartDate, endOfWeek(weekStartDate));
    }
    return formatMonthYear(monthRefDate);
  }, [viewMode, selectedDate, weekStartDate, monthRefDate]);

  const navLabel = useMemo(() => {
    if (viewMode === 'day') return formatShortDate(selectedDate);
    if (viewMode === 'week') {
      const s = weekStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const e = endOfWeek(weekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${s} – ${e}`;
    }
    return monthRefDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }, [viewMode, selectedDate, weekStartDate, monthRefDate]);

  const goToToday = () => {
    setSelectedDate(getLocalDateString());
  };

  const isToday = selectedDate === getLocalDateString();

  const handleOpenModal = async (entry?: TimeEntry) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        project_id: entry.project_id,
        description: entry.description,
        hours: String(entry.hours),
        category: entry.category || 'Development',
        date: entry.date,
        start_time: entry.start_time || '',
      });
      setSelectedSubprojectId(entry.subproject_id || '');
      // Load attachments and links for edit modal
      setIsLoadingModalAttachments(true);
      setIsLoadingModalLinks(true);
      try {
        const atts = await attachmentsAPI.list(entry.id);
        setModalAttachments(atts);
        setEntryAttachments(prev => ({ ...prev, [entry.id]: atts }));
      } catch (err) {
        setModalAttachments([]);
      } finally {
        setIsLoadingModalAttachments(false);
      }
      try {
        const lnks = await linksAPI.list(entry.id);
        setModalLinks(lnks);
        setEntryLinks(prev => ({ ...prev, [entry.id]: lnks }));
      } catch (err) {
        setModalLinks([]);
      } finally {
        setIsLoadingModalLinks(false);
      }
    } else {
      setEditingEntry(null);
      setFormData({
        project_id: '',
        description: '',
        hours: '',
        category: '',
        date: selectedDate,
        start_time: '',
      });
      setSelectedSubprojectId('');
      setModalAttachments([]);
      setModalLinks([]);
    }
    setFormError('');
    setAttachmentError(null);
    setLinkError(null);
    setNewLinkUrl('');
    setNewLinkTitle('');
    setIsModalOpen(true);
  };

  const handleCopyEntry = (entry: TimeEntry) => {
    setEditingEntry(null);
    setIsCopying(true);
    setFormData({
      project_id: entry.project_id,
      description: entry.description,
      hours: String(entry.hours),
      category: entry.category || 'Development',
      date: selectedDate,
      start_time: entry.start_time || '',
    });
    setSelectedSubprojectId(entry.subproject_id || '');
    setModalAttachments([]);
    setModalLinks([]);
    setFormError('');
    setAttachmentError(null);
    setLinkError(null);
    setNewLinkUrl('');
    setNewLinkTitle('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setIsCopying(false);
    setFormData({ project_id: '', description: '', hours: '', category: '', date: selectedDate, start_time: '' });
    setSelectedSubprojectId('');
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.project_id) {
      setFormError('Please select a project');
      return;
    }
    if (!formData.category) {
      setFormError('Please select a category');
      return;
    }
    if (!formData.description.trim()) {
      setFormError('Please enter a description');
      return;
    }
    const hours = parseFloat(formData.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      setFormError(t('errors.invalidHours'));
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingEntry) {
        await updateEntry(editingEntry.id, {
          project_id: formData.project_id,
          date: formData.date,
          description: formData.description.trim(),
          hours: parseFloat(hours.toFixed(2)),
          category: formData.category as TimeEntryCategory,
          start_time: formData.start_time || null,
          // Stamp the zone you're logging in when a start time is present
          start_time_tz: formData.start_time ? currentZone : null,
          subproject_id: selectedSubprojectId || null,
        });
      } else {
        await createEntry({
          project_id: formData.project_id,
          date: formData.date,
          description: formData.description.trim(),
          hours: parseFloat(hours.toFixed(2)),
          category: formData.category as TimeEntryCategory,
          start_time: formData.start_time || null,
          start_time_tz: formData.start_time ? currentZone : null,
          subproject_id: selectedSubprojectId || null,
        });
      }

      handleCloseModal();
      // Refresh range view if active
      if (viewMode !== 'day') refreshRange();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      // Handle Pydantic validation errors (array of {loc, msg, type})
      if (Array.isArray(detail)) {
        const messages = detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        setFormError(messages || t('errors.generic'));
      } else {
        setFormError(detail || t('errors.generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (entry: TimeEntry) => {
    setDeletingEntry(entry);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingEntry) return;

    setIsSubmitting(true);
    try {
      await deleteEntry(deletingEntry.id);
      setIsDeleteModalOpen(false);
      setDeletingEntry(null);
      if (viewMode !== 'day') refreshRange();
    } catch (err) {
      // Error is handled by the store
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group projects by client for the dropdown
  const groupedProjects = clients.map(client => ({
    client,
    projects: projects.filter(p => p.client_id === client.id && p.is_active),
  })).filter(group => group.projects.length > 0);

  const activeProjects = projects.filter(p => p.is_active);
  const activeCategories = projectCategories.filter(c => c.is_active);

  // Get category color by name (from form categories or daily entry color map)
  const getCategoryColor = (categoryName: string): string => {
    const cat = projectCategories.find(c => c.name === categoryName);
    return cat?.color || categoryColorMap[categoryName] || '#6B7280';
  };

  // Get the effective timestamp (later of created_at or updated_at)
  const getEffectiveTimestamp = (entry: TimeEntry): Date => {
    const created = new Date(entry.created_at);
    const updated = new Date(entry.updated_at);
    return updated > created ? updated : created;
  };

  // Format the entry's own date (the day it is logged into), not its created/updated timestamp.
  // Parse with an explicit local midnight to avoid a UTC shift moving the date a day.
  const formatEntryDate = (entry: TimeEntry): string => {
    const entryDate = new Date(entry.date + 'T00:00:00');
    return entryDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Sort entries based on current sort field and direction
  const getSortedEntries = (entries: TimeEntry[]): TimeEntry[] => {
    if (!entries || entries.length === 0) return entries;

    return [...entries].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'modified':
          comparison = getEffectiveTimestamp(a).getTime() - getEffectiveTimestamp(b).getTime();
          break;
        case 'start_time':
          // Entries without start_time go to end (asc) or beginning (desc)
          const aTime = a.start_time;
          const bTime = b.start_time;
          if (!aTime && !bTime) {
            comparison = 0;
          } else if (!aTime) {
            comparison = sortDirection === 'asc' ? 1 : -1;
          } else if (!bTime) {
            comparison = sortDirection === 'asc' ? -1 : 1;
          } else {
            comparison = aTime.localeCompare(bTime);
          }
          break;
        case 'category':
          const aCat = a.category || 'Development';
          const bCat = b.category || 'Development';
          comparison = aCat.localeCompare(bCat);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Handler for timer stop
  const handleTimerStop = async (data: {
    hours: number;
    projectId: string | null;
    categoryId: string | null;
    subprojectId: string | null;
    description: string;
  }) => {
    if (!data.projectId || data.hours <= 0) return;

    // Fetch categories and subprojects for the timer's project
    let categoryName = 'Development';
    try {
      const [cats, subs] = await Promise.all([
        categoriesAPI.list(data.projectId),
        subprojectsAPI.list(data.projectId),
      ]);
      setProjectCategories(cats);
      setSubprojects(subs);
      const category = cats.find(c => c.id === data.categoryId);
      if (category) categoryName = category.name;
    } catch (err) {
      console.error('Failed to fetch project config for timer entry:', err);
    }

    // Open the modal with pre-filled data
    setEditingEntry(null);
    setFormData({
      project_id: data.projectId,
      description: data.description || '',
      hours: data.hours.toFixed(2),
      category: categoryName as TimeEntryCategory,
      date: selectedDate,
      start_time: '',
    });
    setSelectedSubprojectId(data.subprojectId || '');
    setFormError('');
    setIsModalOpen(true);
  };

  // Handler for quick add template
  const handleQuickAdd = async (templateId: string) => {
    setShowQuickAdd(false);
    try {
      // Pass the zone you're currently in so a Quick Add anchored in another
      // zone is converted to your local time (e.g. 8:30 MT scrum -> 10:30 ET).
      await applyTemplate(templateId, { date: selectedDate, target_tz: currentZone });
      await fetchDailySummary(selectedDate);
    } catch (err: any) {
      // Error handled by store
    }
  };

  // Handler for bulk/csv success
  const handleBulkSuccess = () => {
    fetchDailySummary(selectedDate);
  };

  // ─── Attachment handlers ───

  const handleToggleAttachments = async (entryId: string) => {
    if (expandedEntryId === entryId) {
      setExpandedEntryId(null);
      return;
    }
    setExpandedEntryId(entryId);
    if (!entryAttachments[entryId]) {
      try {
        const atts = await attachmentsAPI.list(entryId);
        setEntryAttachments(prev => ({ ...prev, [entryId]: atts }));
      } catch (err) {
        console.error('Failed to fetch attachments:', err);
      }
    }
    if (!entryLinks[entryId]) {
      try {
        const lnks = await linksAPI.list(entryId);
        setEntryLinks(prev => ({ ...prev, [entryId]: lnks }));
      } catch (err) {
        console.error('Failed to fetch links:', err);
      }
    }
  };

  const handleUploadAttachment = async (entryId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploadingAttachment(true);
    setAttachmentError(null);

    try {
      for (const file of Array.from(files)) {
        if (file.size > 5 * 1024 * 1024) {
          setAttachmentError(`File "${file.name}" exceeds 5 MB limit`);
          continue;
        }
        const attachment = await attachmentsAPI.upload(entryId, file);
        setEntryAttachments(prev => ({
          ...prev,
          [entryId]: [...(prev[entryId] || []), attachment],
        }));
        if (editingEntry?.id === entryId) {
          setModalAttachments(prev => [...prev, attachment]);
        }
      }
      await fetchDailySummary(selectedDate);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setAttachmentError(typeof detail === 'string' ? detail : 'Failed to upload file');
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string, entryId: string) => {
    try {
      await attachmentsAPI.delete(attachmentId);
      setEntryAttachments(prev => ({
        ...prev,
        [entryId]: (prev[entryId] || []).filter(a => a.id !== attachmentId),
      }));
      setModalAttachments(prev => prev.filter(a => a.id !== attachmentId));
      await fetchDailySummary(selectedDate);
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      const blob = await attachmentsAPI.download(attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download attachment:', err);
    }
  };

  const handleViewPdf = async (attachment: Attachment) => {
    try {
      const blob = await attachmentsAPI.download(attachment.id);
      const url = URL.createObjectURL(blob);
      setPdfViewerUrl(url);
      setPdfViewerFilename(attachment.original_filename);
      setPdfViewerAttachment(attachment);
      setPdfViewerOpen(true);
    } catch (err) {
      console.error('Failed to load PDF:', err);
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    if (pdfViewerUrl) {
      URL.revokeObjectURL(pdfViewerUrl);
      setPdfViewerUrl(null);
    }
    setPdfViewerAttachment(null);
    setPdfViewerDailyNote(null);
  };

  // ─── Daily "Other Notes" handlers ───

  const handleUploadDailyNote = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploadingDailyNote(true);
    setDailyNoteError(null);

    try {
      for (const file of Array.from(files)) {
        if (file.size > 5 * 1024 * 1024) {
          setDailyNoteError(`File "${file.name}" exceeds 5 MB limit`);
          continue;
        }
        const note = await dailyNotesAPI.upload(selectedDate, file);
        setDailyNotes(prev => [...prev, note]);
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setDailyNoteError(typeof detail === 'string' ? detail : 'Failed to upload file');
    } finally {
      setIsUploadingDailyNote(false);
    }
  };

  const handleDeleteDailyNote = async (noteId: string) => {
    try {
      await dailyNotesAPI.delete(noteId);
      setDailyNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const handleDownloadDailyNote = async (note: DailyNote) => {
    try {
      const blob = await dailyNotesAPI.download(note.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = note.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download note:', err);
    }
  };

  const handleViewDailyNotePdf = async (note: DailyNote) => {
    try {
      const blob = await dailyNotesAPI.download(note.id);
      const url = URL.createObjectURL(blob);
      setPdfViewerUrl(url);
      setPdfViewerFilename(note.original_filename);
      setPdfViewerDailyNote(note);
      setPdfViewerAttachment(null);
      setPdfViewerOpen(true);
    } catch (err) {
      console.error('Failed to load PDF:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ─── Link handlers ───

  const handleAddLink = async (entryId: string) => {
    if (!newLinkUrl.trim()) return;
    setLinkError(null);
    try {
      const link = await linksAPI.create(entryId, {
        url: newLinkUrl.trim(),
        title: newLinkTitle.trim() || undefined,
      });
      setEntryLinks(prev => ({
        ...prev,
        [entryId]: [...(prev[entryId] || []), link],
      }));
      setModalLinks(prev => [...prev, link]);
      setNewLinkUrl('');
      setNewLinkTitle('');
      await fetchDailySummary(selectedDate);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setLinkError(typeof detail === 'string' ? detail : 'Failed to add link');
    }
  };

  const handleDeleteLink = async (linkId: string, entryId: string) => {
    try {
      await linksAPI.delete(linkId);
      setEntryLinks(prev => ({
        ...prev,
        [entryId]: (prev[entryId] || []).filter(l => l.id !== linkId),
      }));
      setModalLinks(prev => prev.filter(l => l.id !== linkId));
      await fetchDailySummary(selectedDate);
    } catch (err) {
      console.error('Failed to delete link:', err);
    }
  };

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const selected = new Date(selectedDate + 'T00:00:00');
    const year = selected.getFullYear();
    const month = selected.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const todayStr = getLocalDateString();

    const days: { date: string; isCurrentMonth: boolean; isSelected: boolean; isToday: boolean }[] = [];

    // Add days from previous month
    const startDayOfWeek = firstDay.getDay();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      const dateStr = getLocalDateString(date);
      days.push({
        date: dateStr,
        isCurrentMonth: false,
        isSelected: false,
        isToday: dateStr === todayStr,
      });
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const dateStr = getLocalDateString(date);
      days.push({
        date: dateStr,
        isCurrentMonth: true,
        isSelected: dateStr === selectedDate,
        isToday: dateStr === todayStr,
      });
    }

    // Add days from next month
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      const dateStr = getLocalDateString(date);
      days.push({
        date: dateStr,
        isCurrentMonth: false,
        isSelected: false,
        isToday: dateStr === todayStr,
      });
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const current = new Date(selectedDate + 'T00:00:00');
    if (direction === 'prev') {
      current.setMonth(current.getMonth() - 1);
    } else {
      current.setMonth(current.getMonth() + 1);
    }
    setSelectedDate(getLocalDateString(current));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Date Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('timeEntry.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {headerLabel}
          </p>
          {/* Current logging zone — auto-detected, overridable. Start times are
              stamped with this zone and Quick Adds are converted into it. */}
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Logging in</span>
            <select
              value={currentZone}
              onChange={(e) => {
                const z = e.target.value;
                if (z === '__auto__') resetToDevice();
                else setOverride(z);
              }}
              className="bg-transparent border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-300 focus:ring-1 focus:ring-green-500 focus:border-transparent"
              title="The time zone you're currently logging in"
            >
              {!COMMON_ZONES.some((z) => z.id === currentZone) && (
                <option value={currentZone}>{zoneLabel(currentZone, selectedDate)}</option>
              )}
              {COMMON_ZONES.map((z) => (
                <option key={z.id} value={z.id}>
                  {zoneLabel(z.id, selectedDate)}
                </option>
              ))}
            </select>
            {isAuto ? (
              <span className="text-gray-400 dark:text-gray-500">· auto</span>
            ) : (
              <button
                type="button"
                onClick={resetToDevice}
                className="text-green-600 dark:text-green-400 hover:underline"
                title={`Reset to your device zone (${zoneLabel(deviceZone, selectedDate)})`}
              >
                · auto
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View mode switcher */}
          <div
            role="tablist"
            aria-label="Time entry view mode"
            className="inline-flex items-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5"
          >
            {(['day', 'week', 'month'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={viewMode === mode}
                onClick={() => setViewMode(mode)}
                className={
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors ' +
                  (viewMode === mode
                    ? 'bg-green-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700')
                }
              >
                {mode === 'day' ? 'Day' : mode === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>

          {/* Date Navigation */}
          <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => navigateDate('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-lg transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <CalendarIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {navLabel}
              </span>
            </button>
            <button
              onClick={() => navigateDate('next')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg transition-colors"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {!isToday && (
            <button
              onClick={goToToday}
              className="px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
            >
              Today
            </button>
          )}

          {/* Quick Add Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowQuickAdd(!showQuickAdd)}
              className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
              title="Quick Add from Templates"
            >
              <BookmarkIcon className="h-4 w-4 mr-1" />
              Quick Add
              <ChevronDownIcon className="h-4 w-4 ml-1" />
            </button>
            {showQuickAdd && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="py-1 max-h-72 overflow-y-auto">
                  {templates.length > 0 ? (
                    templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleQuickAdd(template.id)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {template.is_favorite && (
                            <StarIconSolid className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                          )}
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {template.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {template.project_name}
                          </span>
                          {template.default_hours && (
                            <span className="text-xs text-gray-400">
                              ({template.default_hours}h)
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      No templates yet. Create one to quickly add entries.
                    </div>
                  )}
                  <div className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1">
                    <button
                      onClick={() => {
                        setShowQuickAdd(false);
                        setIsTemplateManagerOpen(true);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Manage Templates...
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bulk Entry Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsBulkEntryOpen(true)}
              disabled={activeProjects.length === 0}
              className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:bg-gray-300 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
              title="Add multiple entries"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
          </div>

          {/* CSV Import Button */}
          <button
            onClick={() => setIsCsvImportOpen(true)}
            disabled={activeProjects.length === 0}
            className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:bg-gray-300 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
            title="Import from CSV"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
          </button>

          <button
            onClick={() => handleOpenModal()}
            disabled={activeProjects.length === 0}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('timeEntry.addEntry')}
          </button>
        </div>
      </div>

      {/* Timer Section */}
      <Timer onTimerStop={handleTimerStop} />

      {/* Calendar Dropdown */}
      {showCalendar && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 max-w-sm">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <button
              onClick={() => navigateMonth('next')}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="py-1">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {generateCalendarDays().map((day, i) => (
              <button
                key={i}
                onClick={() => {
                  setSelectedDate(day.date);
                  setShowCalendar(false);
                }}
                className={`p-2 text-sm rounded transition-colors ${
                  day.isSelected
                    ? 'bg-green-600 text-white'
                    : day.isToday
                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                    : day.isCurrentMonth
                    ? 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    : 'text-gray-400 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {new Date(day.date + 'T00:00:00').getDate()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No projects warning */}
      {activeProjects.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            You need to create a client and project before you can log time.
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button onClick={clearError} className="text-sm text-red-800 dark:text-red-300 underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Day view — daily summary + entries list */}
      {viewMode === 'day' && (
      <>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <ClockIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('timeEntry.dailyTotal')}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {dailySummary ? Number(dailySummary.total_hours).toFixed(2) : '0.00'} hours
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {dailySummary?.entry_count || 0} entries
            </p>
          </div>
        </div>
      </div>

      {/* Time Entries List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Time Entries
          </h2>

          {/* Sort Controls */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Sort by:</span>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value as 'modified' | 'start_time' | 'category')}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="modified">Created/Edited</option>
              <option value="start_time">Start Time</option>
              <option value="category">Category</option>
            </select>
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={sortDirection === 'asc' ? 'Ascending (oldest first)' : 'Descending (newest first)'}
            >
              {sortDirection === 'asc' ? (
                <BarsArrowUpIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <BarsArrowDownIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : dailySummary && dailySummary.entries.length > 0 ? (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {getSortedEntries(dailySummary.entries).map((entry) => (
              <div
                key={entry.id}
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatEntryDate(entry)}
                      </span>
                      {entry.start_time && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <span className="text-xs text-green-600 dark:text-green-400">
                            Start: {formatStartWithZone(entry.start_time, entry.start_time_tz, entry.date)}
                          </span>
                        </>
                      )}
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {entry.client_name}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">/</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {entry.project_name}
                      </span>
                      {entry.subproject_name && (
                        <>
                          <span className="text-gray-400 dark:text-gray-500">/</span>
                          <span className="text-sm text-blue-600 dark:text-blue-400">
                            {entry.subproject_name}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      {entry.description}
                    </p>
                    {((entry.attachment_count ?? 0) > 0 || (entry.link_count ?? 0) > 0) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleAttachments(entry.id);
                        }}
                        className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        title={t('attachments.viewAttachments')}
                      >
                        {(entry.attachment_count ?? 0) > 0 && (
                          <>
                            <PaperClipIcon className="h-3.5 w-3.5" />
                            <span>{entry.attachment_count} {entry.attachment_count === 1 ? t('attachments.file') : t('attachments.files')}</span>
                          </>
                        )}
                        {(entry.link_count ?? 0) > 0 && (
                          <>
                            <LinkIcon className="h-3.5 w-3.5" />
                            <span>{entry.link_count} {entry.link_count === 1 ? t('links.link') : t('links.links')}</span>
                          </>
                        )}
                        <ChevronDownIcon className={`h-3 w-3 transition-transform ${expandedEntryId === entry.id ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  <div className="ml-4 flex items-center space-x-3">
                    <span
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${getCategoryColor(entry.category || 'Development')}20`,
                        color: getCategoryColor(entry.category || 'Development'),
                      }}
                    >
                      {entry.category || 'Development'}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {Number(entry.hours).toFixed(2)} hrs
                    </span>
                    <button
                      onClick={() => handleOpenModal(entry)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={t('actions.edit')}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleCopyEntry(entry)}
                      className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={t('timeEntry.copyEntry')}
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(entry)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={t('actions.delete')}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* Expandable attachment list */}
                {expandedEntryId === entry.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="space-y-2">
                      {(entryAttachments[entry.id] || []).map((att) => (
                        <div key={att.id} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate text-gray-700 dark:text-gray-300">{att.original_filename}</span>
                            <span className="text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">({formatFileSize(att.file_size)})</span>
                            {att.ocr_status === 'completed' && (
                              <DocumentTextIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" title={t('attachments.ocrComplete', 'Text extracted')} />
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {att.mime_type === 'application/pdf' && (
                              <button
                                onClick={() => handleViewPdf(att)}
                                className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                                title="View PDF"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDownloadAttachment(att)}
                              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              title={t('attachments.download')}
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAttachment(att.id, entry.id)}
                              className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              title={t('actions.delete')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {(entryAttachments[entry.id] || []).length === 0 && (entry.attachment_count ?? 0) > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('attachments.noAttachments')}</p>
                      )}
                    </div>
                    {/* Links */}
                    {(entryLinks[entry.id] || []).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {(entryLinks[entry.id] || []).map((link) => (
                          <div key={link.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              <LinkIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="truncate text-blue-600 dark:text-blue-400 hover:underline"
                                title={link.url}
                              >
                                {link.title || link.url}
                              </a>
                            </div>
                            <button
                              onClick={() => handleDeleteLink(link.id, entry.id)}
                              className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                              title={t('actions.delete')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              {t('timeEntry.noEntries')}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('timeEntry.noEntriesDescription')}
            </p>
            {activeProjects.length > 0 && (
              <button
                onClick={() => handleOpenModal()}
                className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('timeEntry.addEntry')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Other Notes — standalone PDFs for this day (not tied to a meeting) */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('otherNotes.title', 'Other Notes')}
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {dailyNotes.length > 0 ? `(${dailyNotes.length})` : ''}
            </span>
          </div>
          {dailyNotes.length < 20 && (
            <label
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-colors ${
                isUploadingDailyNote
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              {isUploadingDailyNote
                ? t('otherNotes.uploading', 'Uploading…')
                : t('otherNotes.upload', 'Upload PDF')}
              <input
                type="file"
                accept=".pdf"
                multiple
                disabled={isUploadingDailyNote}
                onChange={(e) => {
                  handleUploadDailyNote(e.target.files);
                  e.target.value = '';
                }}
                className="hidden"
              />
            </label>
          )}
        </div>

        {dailyNoteError && (
          <div className="px-6 pt-3 text-sm text-red-600 dark:text-red-400">{dailyNoteError}</div>
        )}

        {isLoadingDailyNotes ? (
          <div className="p-6 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : dailyNotes.length > 0 ? (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {dailyNotes.map((note) => (
              <div
                key={note.id}
                className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate text-sm text-gray-700 dark:text-gray-300">
                    {note.original_filename}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">
                    ({formatFileSize(note.file_size)})
                  </span>
                  {note.ocr_status === 'completed' && (
                    <DocumentTextIcon
                      className="h-3.5 w-3.5 text-green-500 flex-shrink-0"
                      title={t('attachments.ocrComplete', 'Text extracted')}
                    />
                  )}
                  {note.ocr_status === 'processing' && (
                    <span className="text-xs text-gray-400 flex-shrink-0">{t('otherNotes.ocrProcessing', 'OCR…')}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleViewDailyNotePdf(note)}
                    className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                    title="View PDF"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDownloadDailyNote(note)}
                    className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    title={t('attachments.download')}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteDailyNote(note.id)}
                    className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    title={t('actions.delete')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-10 text-center">
            <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-600" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('otherNotes.empty', 'No other notes for this day. Upload PDFs (notes, todos, other content) to keep them here — they’re OCR’d and searchable.')}
            </p>
          </div>
        )}
      </div>
      </>
      )}

      {/* Week view */}
      {viewMode === 'week' && (
        <WeekView
          weekStart={weekStartDate}
          entriesByDate={entriesByDate}
          todayIso={getLocalDateString()}
          onSelectDate={(d) => {
            setSelectedDate(d);
            setViewMode('day');
          }}
          onEditEntry={(entry) => handleOpenModal(entry)}
          onNewEntry={(d) => {
            setSelectedDate(d);
            handleOpenModal();
          }}
          isLoading={isLoadingRange}
        />
      )}

      {/* Month view */}
      {viewMode === 'month' && (
        <MonthView
          monthRefDate={monthRefDate}
          entriesByDate={entriesByDate}
          todayIso={getLocalDateString()}
          onSelectDate={(d) => {
            setSelectedDate(d);
            setViewMode('day');
          }}
          isLoading={isLoadingRange}
        />
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingEntry ? t('timeEntry.editEntry') : isCopying ? t('timeEntry.copyEntry') : t('timeEntry.addEntry')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('timeEntry.selectProject')}
            </label>
            <select
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select a project...</option>
              {groupedProjects.map((group) => (
                <optgroup key={group.client.id} label={group.client.name}>
                  {group.projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Time <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent dark:[color-scheme:dark]"
                placeholder="HH:MM"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as TimeEntryCategory })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select a category...</option>
              {activeCategories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subproject Dropdown */}
          {formData.project_id && subprojects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subproject <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                value={selectedSubprojectId}
                onChange={(e) => setSelectedSubprojectId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">No subproject</option>
                {subprojects.map((s: ProjectSubproject) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('timeEntry.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What did you work on?"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Attachments Section (only when editing) */}
          {editingEntry && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('attachments.title')} <span className="text-gray-400 font-normal">({t('attachments.maxFiles')})</span>
              </label>

              {isLoadingModalAttachments ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('actions.loading')}</div>
              ) : (
                <div className="space-y-1 mb-2">
                  {modalAttachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700 rounded px-3 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate text-gray-700 dark:text-gray-300">{att.original_filename}</span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">({formatFileSize(att.file_size)})</span>
                        {att.ocr_status === 'completed' && (
                          <DocumentTextIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" title={t('attachments.ocrComplete', 'Text extracted')} />
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {att.mime_type === 'application/pdf' && (
                          <button
                            type="button"
                            onClick={() => handleViewPdf(att)}
                            className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                            title="View PDF"
                          >
                            <EyeIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDownloadAttachment(att)}
                          className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title={t('attachments.download')}
                        >
                          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(att.id, editingEntry.id)}
                          className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title={t('actions.delete')}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {modalAttachments.length < 5 && (
                <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg cursor-pointer transition-colors">
                  <PaperClipIcon className="h-4 w-4" />
                  {isUploadingAttachment ? t('actions.loading') : t('attachments.addFile')}
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.txt"
                    multiple
                    disabled={isUploadingAttachment}
                    onChange={(e) => {
                      handleUploadAttachment(editingEntry.id, e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}

              {attachmentError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{attachmentError}</p>
              )}

              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {t('attachments.allowedTypes')}
              </p>
            </div>
          )}

          {/* Links Section (only when editing) */}
          {editingEntry && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('links.title')} <span className="text-gray-400 font-normal">({t('links.maxLinks')})</span>
              </label>

              {isLoadingModalLinks ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">{t('actions.loading')}</div>
              ) : (
                <div className="space-y-1 mb-2">
                  {modalLinks.map((link) => (
                    <div key={link.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700 rounded px-3 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <LinkIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate text-blue-600 dark:text-blue-400 hover:underline"
                          title={link.url}
                        >
                          {link.title || link.url}
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteLink(link.id, editingEntry.id)}
                        className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                        title={t('actions.delete')}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {modalLinks.length < 10 && (
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newLinkUrl}
                    onChange={(e) => setNewLinkUrl(e.target.value)}
                    placeholder={t('links.urlPlaceholder')}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={newLinkTitle}
                    onChange={(e) => setNewLinkTitle(e.target.value)}
                    placeholder={t('links.titlePlaceholder')}
                    className="w-1/3 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddLink(editingEntry.id)}
                    disabled={!newLinkUrl.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-lg transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {t('links.addLink')}
                  </button>
                </div>
              )}

              {linkError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{linkError}</p>
              )}
            </div>
          )}

          <Input
            label={t('timeEntry.hours')}
            type="number"
            step="0.01"
            min="0.01"
            max="24"
            value={formData.hours}
            onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            placeholder="e.g., 1.5"
            error={formError}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={handleCloseModal} type="button">
              {t('actions.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {t('actions.save')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('actions.delete')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t('timeEntry.deleteConfirm')}
          </p>
          {deletingEntry && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <p className="text-sm text-gray-900 dark:text-white font-medium">
                {deletingEntry.description}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {Number(deletingEntry.hours).toFixed(2)} hours
              </p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} isLoading={isSubmitting}>
              {t('actions.delete')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Template Manager Modal */}
      <TemplateManager
        isOpen={isTemplateManagerOpen}
        onClose={() => setIsTemplateManagerOpen(false)}
      />

      {/* Bulk Entry Modal */}
      <BulkEntryModal
        isOpen={isBulkEntryOpen}
        onClose={() => setIsBulkEntryOpen(false)}
        onSuccess={handleBulkSuccess}
        initialDate={selectedDate}
      />

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={isCsvImportOpen}
        onClose={() => setIsCsvImportOpen(false)}
        onSuccess={handleBulkSuccess}
      />

      {/* PDF Viewer Modal */}
      {pdfViewerUrl && (
        <PdfViewerModal
          isOpen={pdfViewerOpen}
          onClose={handleClosePdfViewer}
          pdfUrl={pdfViewerUrl}
          filename={pdfViewerFilename}
          onDownload={
            pdfViewerAttachment
              ? () => handleDownloadAttachment(pdfViewerAttachment)
              : pdfViewerDailyNote
              ? () => handleDownloadDailyNote(pdfViewerDailyNote)
              : undefined
          }
          ocrText={pdfViewerAttachment?.ocr_text ?? pdfViewerDailyNote?.ocr_text}
          ocrMarkdown={pdfViewerAttachment?.ocr_markdown ?? pdfViewerDailyNote?.ocr_markdown}
          ocrStatus={pdfViewerAttachment?.ocr_status ?? pdfViewerDailyNote?.ocr_status}
          ocrMethod={pdfViewerAttachment?.ocr_method ?? pdfViewerDailyNote?.ocr_method}
          onRetryOcr={
            pdfViewerAttachment
              ? async () => {
                  try {
                    const result = await attachmentsAPI.retryOcr(pdfViewerAttachment.id);
                    setPdfViewerAttachment(prev => (prev ? { ...prev, ...result } : prev));
                  } catch (err) {
                    console.error('OCR re-run failed:', err);
                  }
                }
              : pdfViewerDailyNote
              ? async () => {
                  try {
                    const result = await dailyNotesAPI.retryOcr(pdfViewerDailyNote.id);
                    setPdfViewerDailyNote(prev => (prev ? { ...prev, ...result } : prev));
                    setDailyNotes(prev => prev.map(n => (n.id === pdfViewerDailyNote.id ? { ...n, ...result } : n)));
                  } catch (err) {
                    console.error('OCR re-run failed:', err);
                  }
                }
              : undefined
          }
          onRefreshOcr={
            pdfViewerAttachment
              ? async () => {
                  const result = await attachmentsAPI.getOcr(pdfViewerAttachment.id);
                  setPdfViewerAttachment(prev => (prev ? { ...prev, ...result } : prev));
                }
              : pdfViewerDailyNote
              ? async () => {
                  const result = await dailyNotesAPI.getOcr(pdfViewerDailyNote.id);
                  setPdfViewerDailyNote(prev => (prev ? { ...prev, ...result } : prev));
                  setDailyNotes(prev => prev.map(n => (n.id === pdfViewerDailyNote.id ? { ...n, ...result } : n)));
                }
              : undefined
          }
          onSaveCorrection={
            pdfViewerAttachment
              ? async (md) => {
                  const result = await attachmentsAPI.saveCorrection(pdfViewerAttachment.id, md);
                  setPdfViewerAttachment(prev => (prev ? { ...prev, ...result } : prev));
                }
              : pdfViewerDailyNote
              ? async (md) => {
                  const result = await dailyNotesAPI.saveCorrection(pdfViewerDailyNote.id, md);
                  setPdfViewerDailyNote(prev => (prev ? { ...prev, ...result } : prev));
                  setDailyNotes(prev => prev.map(n => (n.id === pdfViewerDailyNote.id ? { ...n, ...result } : n)));
                }
              : undefined
          }
        />
      )}

      {/* Click outside handler for Quick Add dropdown */}
      {showQuickAdd && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowQuickAdd(false)}
        />
      )}
    </div>
  );
};
